import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Modal, Form, Input, message, Rate, Upload, Popover } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { UploadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import * as api from '@apis/program-report';
import { GetProgramReport, Intelligence, ProgramReport, ProgramReportStatus } from '@models/programReport';
import { INTELLIGENCE_TYPE_LABEL, IntelligenceType } from '@models/programReport';
import { useAuthentication } from '@hooks/authentication';
import { cdnImageUrl, resizeImage } from '@utils/image';
import moment from 'moment';

const ProgramReportsPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingReport, setEditingReport] = useState<GetProgramReport | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  useAuthentication();

  const { data, isLoading } = useQuery({
    queryKey: ['program-reports', currentPage, pageSize, searchKeyword],
    queryFn: () => api.searchProgramReports({ keyword: searchKeyword, page: currentPage - 1, size: pageSize }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ reportId, formData }: { reportId: string; formData: FormData }) =>
      api.updateProgramReport(reportId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program-reports'] });
      setIsModalVisible(false);
      message.success('리포트가 수정되었습니다.');
    },
    onError: (error: any) => message.error(error.message || '오류가 발생했습니다.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (reportId: string) => api.deleteProgramReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program-reports'] });
      message.success('리포트가 삭제되었습니다.');
    },
  });

  const handleEdit = (report: GetProgramReport) => {
    setEditingReport(report);
    const initialFileList = (report.imageUrls || []).map((url, index) => ({
      uid: `-` + index,
      name: `image-${index}.png`,
      status: 'done',
      url: cdnImageUrl(url),
      response: url, // To keep track of original url
    }));
    setFileList(initialFileList);
    form.setFieldsValue({
      ...report,
      ...(report.intelligences
        ? Object.keys(report.intelligences).reduce((acc, key) => {
            const intelligence = report.intelligences[key as IntelligenceType];
            acc[`${key}_score`] = intelligence?.score ?? null;
            acc[`${key}_comment`] = intelligence?.comment ?? '';
            return acc;
          }, {} as any)
        : {}),
    });
    setIsModalVisible(true);
  };

  useEffect(() => {
    if (editingReport) {
      handleEdit(editingReport);
    }
  }, [editingReport]);

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: '정말로 이 리포트를 삭제하시겠습니까?',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();

      const newFiles = fileList.filter((file) => file.originFileObj);
      const existingImageUrls = fileList.filter((file) => file.url && !file.originFileObj).map((file) => file.response);
      const placeholderFileNames = newFiles.map((file) => file.name);

      const jsonBody: Partial<ProgramReport> = {
        childName: values.childName,
        comment: values.comment,
        intelligences: Object.keys(INTELLIGENCE_TYPE_LABEL).reduce((acc, key) => {
          acc[key as IntelligenceType] = {
            score: values[`${key}_score`],
            comment: values[`${key}_comment`],
          };
          return acc;
        }, {} as any),
        imageUrls: [...existingImageUrls, ...placeholderFileNames],
      };

      newFiles.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });

      formData.append('json', JSON.stringify(jsonBody));

      if (!editingReport) return;

      updateMutation.mutate({ reportId: editingReport._id, formData });
    } catch (info) {
      console.log('Validate Failed:', info);
    }
  };

  const columns: ColumnsType<GetProgramReport> = [
    { title: '유저명', render: (_, record) => record.parent?.profile?.name, align: 'center' },
    { title: '아이명', dataIndex: 'childName', align: 'center' },
    {
      title: '파트너명',
      render: (_, record) => record.partner?.profile?.name,
      align: 'center',
    },
    {
      title: '강사명',
      render: (_, record) => record.instructor?.profile?.name,
      align: 'center',
    },
    {
      title: '상품 및 옵션',
      render: (_, render) => {
        return (
          <Popover
            content={
              <div>
                <div>상품명: {render.productName}</div>
                <div>옵션명: {render.schedule?.schedule?.optionName}</div>
              </div>
            }>
            <div style={{ width: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {render.schedule?.schedule?.optionName}
            </div>
          </Popover>
        );
      },
      align: 'center',
    },
    {
      title: '종합점수',
      dataIndex: 'intelligences',
      render: (intelligences: Intelligence) => {
        if (!intelligences) return 0;
        const score =
          Object.values(intelligences).reduce((acc, item) => acc + (item?.score || 0), 0) /
          Object.keys(intelligences).length;

        return score.toFixed(1);
      },
      align: 'center',
    },
    {
      title: '상태',
      dataIndex: 'status',
      render: (status: ProgramReportStatus) => {
        return status === ProgramReportStatus.PENDING ? '임시저장' : '전송';
      },
      align: 'center',
    },
    {
      title: '생성 / 수정 / 전송',
      dataIndex: 'createdAt',
      render: (createdAt, record) => {
        return (
          <div style={{ width: '120px' }}>
            <div style={{ fontSize: '13px' }}>{moment(createdAt).format('YYYY.MM.DD HH:mm')}</div>
            <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
              {moment(record.updatedAt).format('YYYY.MM.DD HH:mm')}
            </div>
            <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
              {record.sentAt ? moment(record.sentAt).format('YYYY.MM.DD HH:mm') : '전송 안함'}
            </div>
          </div>
        );
      },
    },
    {
      title: '관리',
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Button onClick={() => handleEdit(record)}>수정</Button>
          <Button danger onClick={() => handleDelete(record._id)}>
            삭제
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>리포트 관리</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Input
            className={filterActiveClassName(!!searchKeyword)}
            placeholder="아이 이름, 상품명 검색"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 300 }}
          />
        </div>
      </div>
      <div className="page-content">
        <Table
          columns={columns}
          dataSource={data?.data.contents}
          loading={isLoading}
          rowKey="_id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: data?.data.total,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </div>
      <Modal
        title="리포트 수정"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        confirmLoading={updateMutation.isPending}>
        <Form form={form} layout="vertical" initialValues={editingReport || {}}>
          <Form.Item name="childName" label="아이 이름">
            <Input />
          </Form.Item>
          {editingReport?.product.programReportItems?.map((key) => (
            <Form.Item key={key} label={INTELLIGENCE_TYPE_LABEL[key as IntelligenceType]}>
              <Form.Item name={`${key}_score`} noStyle>
                <Rate />
              </Form.Item>
              <Form.Item name={`${key}_comment`} noStyle>
                <Input.TextArea />
              </Form.Item>
            </Form.Item>
          ))}
          <Form.Item name="comment" label="총평">
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="이미지">
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={({ fileList: newFileList }) => setFileList(newFileList)}
              beforeUpload={async (file) => {
                try {
                  const resized = await resizeImage(file, { maxWidth: 860 });
                  const resizedFile = new File([resized.image], file.name, {
                    type: resized.type,
                  }) as any;
                  resizedFile.uid = file.uid;
                  return resizedFile;
                } catch (error) {
                  console.error('Image resize error:', error);
                  message.error('이미지 리사이징 중 오류가 발생했습니다.');
                  return Upload.LIST_IGNORE;
                }
              }}
              customRequest={({ file, onSuccess }) => {
                // We are not uploading here, but we need to call onSuccess to change file status
                setFileList((prev) => {
                  const newFileList = [...prev];
                  const targetFile = newFileList.find((f) => f.uid === (file as any).uid);
                  if (targetFile) {
                    targetFile.originFileObj = file as any;
                  }
                  return newFileList;
                });

                if (onSuccess) {
                  setTimeout(() => {
                    onSuccess('ok');
                  }, 0);
                }
              }}
              multiple>
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>추가</div>
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProgramReportsPage;
